import { OnaLettermark } from "@/assets/icons/OnaLettermark";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/flexkit/Command";
import { Pill } from "@/components/podkit/status/Pill";
import {
    hasRunnerCapability,
    sortEnvironmentClassEntries,
    useEnvironmentClassEntries,
    type EnvironmentClassEntry,
} from "@/hooks/use-environment-class-entries";
import { CommandLoading } from "cmdk";
import { RunnerCapability, RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useAtomValue } from "jotai";
import { DropdownMenu } from "./DropdownMenu";
import { projectContextAtom } from "./state";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { useDebounce } from "use-debounce";

const searchOptions = {
    includeScore: true,
    keys: ["runner.name", "clazz.displayName", "clazz.description"],
};

export function EnvironmentClassSelector({
    width,
    onSelect,
}: {
    width: number;
    onSelect: (entry: EnvironmentClassEntry) => void;
}) {
    const projectContext = useAtomValue(projectContextAtom);
    const isQueryEnabled = !projectContext || projectContext.environmentClasses.length === 0;
    const { environmentClassEntries, isLoading } = useEnvironmentClassEntries({
        enabled: isQueryEnabled,
    });

    const entriesToChooseFrom = useMemo(() => {
        return projectContext && projectContext.environmentClasses.length > 0
            ? projectContext.environmentClasses
            : environmentClassEntries || [];
    }, [projectContext, environmentClassEntries]);

    const [searchValue, setSearchValue] = useState("");
    const [debouncedSearchValue] = useDebounce(searchValue, 150, {
        trailing: true,
    });
    const searchResults = useMemo(() => {
        const availableEntries = sortEnvironmentClassEntries(entriesToChooseFrom);
        if (!debouncedSearchValue) return availableEntries;
        const fuse = new Fuse(availableEntries, searchOptions);
        return fuse.search(debouncedSearchValue).map((result) => result.item);
    }, [entriesToChooseFrom, debouncedSearchValue]);

    return (
        <DropdownMenu width={width}>
            <Command shouldFilter={false}>
                <CommandInput placeholder="Type to search..." value={searchValue} onValueChange={setSearchValue} />
                <CommandList className="max-h-40">
                    <CommandEmpty className="pb-0 pt-2">No environment classes found</CommandEmpty>
                    <CommandGroup>
                        {isLoading && isQueryEnabled ? (
                            <>
                                <CommandLoading className="sr-only">Loading…</CommandLoading>
                                {[1, 2, 3].map((index) => (
                                    <div
                                        key={`skeleton-${index}`}
                                        className="flex w-full flex-row items-center rounded px-2 py-1.5"
                                    >
                                        <span className="flex size-8 animate-pulse items-center justify-center rounded-[4px] bg-content-tertiary" />
                                        <div className="flex-1 rounded px-3 py-1">
                                            <div className="mb-1 h-4 w-24 animate-pulse rounded bg-content-tertiary" />
                                            <div className="h-3 w-48 animate-pulse rounded bg-content-tertiary" />
                                        </div>
                                    </div>
                                ))}
                            </>
                        ) : (
                            searchResults.map((entry) => (
                                <EnvironmentClassItem key={entry.clazz.id} entry={entry} onSelect={onSelect} />
                            ))
                        )}
                    </CommandGroup>
                </CommandList>
            </Command>
        </DropdownMenu>
    );
}

const EnvironmentClassItem = ({
    entry,
    onSelect,
}: {
    entry: EnvironmentClassEntry;
    onSelect: (entry: EnvironmentClassEntry) => void;
}) => {
    const disabled =
        (entry.runner.status?.phase !== RunnerPhase.ACTIVE && entry.runner.status?.phase !== RunnerPhase.DEGRADED) ||
        !hasRunnerCapability(entry, RunnerCapability.AGENT_EXECUTION);
    return (
        <div className="relative">
            <CommandItem
                disabled={disabled}
                key={entry.clazz.id}
                className="flex w-full flex-row items-center aria-disabled:cursor-not-allowed aria-disabled:opacity-80"
                onSelect={() => {
                    if (!disabled) {
                        onSelect(entry);
                    }
                }}
            >
                <span className="flex size-8 items-center justify-center rounded-[4px] bg-[#1E3C2C]">
                    <OnaLettermark className="size-4 shrink-0 text-[#DBEFDB]" aria-label="Ona Logo" />
                </span>
                <div className="relative flex-1 rounded px-3 py-1 text-start">
                    <span className="select-none text-base">{entry.runner.name}</span>
                    <br />
                    <span className="select-none text-sm opacity-80">
                        {entry.clazz.displayName} • {entry.clazz.description}
                    </span>
                </div>
            </CommandItem>
            {/* unsupported pill cannot be nested inside CommandItem because disabled CommandItem will not trigger hover effect. */}
            {disabled ? (
                <div className="group absolute right-2 top-1/2 z-[10000] flex -translate-y-1/2 select-none items-center">
                    <Pill className="px-0">
                        <span className="grid min-h-0 grid-cols-[min-content_0fr] grid-rows-[min-content] overflow-hidden transition-[grid-template-columns] delay-200 duration-150 group-hover:grid-cols-[0fr_1fr]">
                            <span className="group-hover:scale-70 min-h-0 min-w-0 origin-left text-nowrap px-2 transition-all delay-200 duration-500 group-hover:invisible group-hover:px-0 group-hover:opacity-0 group-hover:blur-sm">
                                Unsupported
                            </span>
                            <span className="scale-70 invisible min-h-0 min-w-0 origin-right text-nowrap opacity-0 blur-sm transition-all delay-200 duration-500 group-hover:visible group-hover:scale-100 group-hover:px-2 group-hover:opacity-100 group-hover:blur-none">
                                This class does not support Ona agent
                            </span>
                        </span>
                    </Pill>
                </div>
            ) : null}
        </div>
    );
};
